import { createTheme, Theme } from '@mui/material/styles';

// Shared design tokens drawn from the approved trade-builder mockup.
const SERIF = "'Fraunces', Georgia, serif";
const SANS = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

// Mockup palette
const MOCK = {
  bg0: '#0b1220',
  bg1: '#111a2e',
  bg2: '#19233b',
  border: '#1f2c4a',
  border2: '#2c3b5e',
  text: '#e8edf7',
  text2: '#a6b2c8',
  muted: '#6b7891',
  accent: '#4d8bff',
  accent2: '#6da4ff',
  green: '#10b981',
  red: '#ef4444',
  amber: '#f59e0b',
};

// Serif display headings + sans body, applied to both themes so the whole app
// shares the mockup's typographic feel.
const sharedTypography = {
  fontFamily: SANS,
  h1: { fontFamily: SERIF, fontSize: '2.25rem', fontWeight: 500, lineHeight: 1.15, letterSpacing: '-0.02em' },
  h2: { fontFamily: SERIF, fontSize: '1.875rem', fontWeight: 500, lineHeight: 1.2, letterSpacing: '-0.02em' },
  h3: { fontFamily: SERIF, fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.3, letterSpacing: '-0.01em' },
  h4: { fontFamily: SERIF, fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.3, letterSpacing: '-0.01em' },
  h5: { fontFamily: SERIF, fontSize: '1.125rem', fontWeight: 500, lineHeight: 1.35 },
  h6: { fontFamily: SANS, fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
  overline: { fontFamily: MONO, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase' as const },
  body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6 },
  body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
};

// Light theme
export const lightTheme = createTheme({
  palette: {
    primary: { main: '#2563EB' },
    secondary: { main: '#64748B' },
    success: { main: MOCK.green },
    warning: { main: MOCK.amber },
    error: { main: MOCK.red },
    background: { default: '#F8FAFC', paper: '#FFFFFF' },
    text: { primary: '#1E293B', secondary: '#64748B' },
  },
  typography: sharedTypography,
  spacing: 4,
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, borderRadius: 8, padding: '8px 16px' },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' } },
      },
    },
    MuiCard: {
      styleOverrides: { root: { borderRadius: 12, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' } },
    },
  },
});

// Dark theme — matches the mockup (deep navy panels, blue accent, serif headings).
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: MOCK.accent, light: MOCK.accent2 },
    secondary: { main: MOCK.text2 },
    success: { main: MOCK.green },
    warning: { main: MOCK.amber },
    error: { main: MOCK.red },
    background: { default: MOCK.bg0, paper: MOCK.bg1 },
    text: { primary: MOCK.text, secondary: MOCK.text2 },
    divider: MOCK.border,
  },
  typography: sharedTypography,
  spacing: 4,
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Subtle top glow matching the mockup background.
          backgroundImage: `radial-gradient(ellipse 80% 50% at 50% -10%, rgba(77,139,255,0.10), transparent 60%)`,
          backgroundAttachment: 'fixed',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, borderRadius: 8, padding: '8px 16px' },
        contained: {
          boxShadow: '0 8px 20px -8px rgba(77,139,255,0.5)',
          '&:hover': { boxShadow: '0 10px 24px -8px rgba(77,139,255,0.6)' },
        },
        outlined: { borderColor: MOCK.border2 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${MOCK.border}`,
          backgroundImage: 'none',
          backgroundColor: MOCK.bg1,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: MOCK.bg1, backgroundImage: 'none', borderBottom: `1px solid ${MOCK.border}` },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: MOCK.bg2,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: MOCK.border2 },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: MOCK.text2 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: { root: { borderColor: MOCK.border } },
    },
  },
});

// Default theme (light)
export const theme = lightTheme;

export interface AppThemePreferences {
  /** Tighter spacing and smaller default controls when true. */
  compactMode?: boolean;
}

/**
 * Resolve the active MUI theme from the user's display preferences.
 * Theme color mode is chosen by ``isDark``; ``compactMode`` tightens spacing and
 * shrinks default control density. Font size is applied separately on the root
 * <html> element (rem-based typography scales automatically) in App.tsx.
 */
export function getAppTheme(isDark: boolean, prefs: AppThemePreferences = {}): Theme {
  const base = isDark ? darkTheme : lightTheme;
  if (!prefs.compactMode) {
    return base;
  }
  return createTheme(base, {
    spacing: 3,
    components: {
      MuiButton: { defaultProps: { size: 'small' } },
      MuiTextField: { defaultProps: { size: 'small', margin: 'dense' } },
      MuiTable: { defaultProps: { size: 'small' } },
      MuiList: { defaultProps: { dense: true } },
      MuiCardContent: {
        styleOverrides: {
          root: { padding: 12, '&:last-child': { paddingBottom: 12 } },
        },
      },
    },
  });
}
